import React, { useEffect } from 'react';
import { FaCube, FaGlobe, FaTh, FaLock, FaPlus, FaMinus, FaObjectGroup, FaCircleNotch, FaEllipsisH, FaRegSquare, FaGripLines,FaCompress, FaExpand, FaCircle, FaDotCircle, FaUserCircle, FaUndo, FaExclamationTriangle } from 'react-icons/fa'; 
import { IconButton, Tooltip } from '@material-ui/core';
import useStyles from './sharedStyles'; 
import { genericTokenIcon, PCAP } from './config';


export class GraphLayoutManager {
  constructor(config) {
    this.mode3D = config.mode3D;
    this.is3DMode = config.is3DMode;
    this.globalDistance = config.globalDistance;
    this.globalSize = config.globalSize;
    this.dataSource = config.dataSource;
    this.pcapGroupEnabled = !!config.pcapGroupEnabled;
    this.setGraphData = config.setGraphData;
    this.activeNodeId = config.activeNodeId;
    this.spinCamera = config.spinCamera;
    this.startCameraSpin = config.startCameraSpin;
    this.stopCameraSpin = config.stopCameraSpin;
    this.graphRef = config.graphRef;
    this.setMode3D = config.setMode3D; // Add setMode3D from config
    this.viewMode = config.viewMode || 'all'; // Store the current view mode
    this.tokenView = config.tokenView || 'transactions'; // Current token view mode
    this.layoutMode = config.layoutMode || 'sphere'; // Current geometric layout mode
    this.setLayoutMode = config.setLayoutMode; // Setter passed from App
    this.resetGraph = config.resetGraph; // Optional graph reset helper from App
    this.setGraphInstanceKey = config.setGraphInstanceKey; // Force remount of ForceGraph
  }


  // Utility: strip any synthetic chain label nodes from the graph so they
  // don't leak into layouts where they are not desired.
  removeChainLabelNodes(graphData) {
    graphData.nodes = graphData.nodes.filter(n => n.nodeType !== 'chain_label');
  }


  distributeIconsInGridOrCube(graphData) {
    this.removeChainLabelNodes(graphData);
    // Check if nodes have nodeType property which indicates they've been classified
    const areNodesClassified = graphData.nodes.some(node => node.nodeType);
    
    // Determine which nodes to arrange based on the current view mode
    let nodesToArrange;
    
    if (areNodesClassified) {
      // Get the current view mode with safeguards
      const viewMode = (typeof this.viewMode !== 'undefined' && this.viewMode !== null) ? this.viewMode : 'all';
      
      // Filter nodes based on the view mode
      if (viewMode === 'all') {
        // All nodes are visible
        nodesToArrange = graphData.nodes;
      } 
      else if (viewMode === 'token_connected') {
        // Only tokens and users connected to tokens are visible
        nodesToArrange = graphData.nodes.filter(node => 
          node.nodeType === 'token' || node.nodeType === 'connected_user'
        );
      } 
      else if (viewMode === 'users_only') {
        // Only users (both connected and isolated) are visible
        nodesToArrange = graphData.nodes.filter(node => 
          node.nodeType === 'connected_user' || node.nodeType === 'isolated_user'
        );
      }
      else if (viewMode === 'tokens_only') {
        // Only tokens with custom icons are visible
        nodesToArrange = graphData.nodes.filter(node => 
          node.nodeType === 'token' && node.icon && node.icon !== genericTokenIcon
        );
      }
      else {
        // Default case: use all nodes
        nodesToArrange = graphData.nodes;
      }
      
      console.log(`Grid layout: arranging ${nodesToArrange.length} nodes out of ${graphData.nodes.length} total nodes with view mode: ${viewMode}`);
    } else {
      // If not in organic layout mode, use all nodes
      nodesToArrange = graphData.nodes;
    }
    
    const totalNodes = nodesToArrange.length;
    const sideLength = this.mode3D ? Math.cbrt(totalNodes) : Math.sqrt(totalNodes); // Use cube root for 3D, square root for 2D
    const nodesPerSide = Math.ceil(sideLength);
    const spacing = this.globalSize / nodesPerSide;

    let index = 0;
    for (let z = 0; z < (this.mode3D ? nodesPerSide : 1) && index < totalNodes; z++) {
        for (let y = 0; y < nodesPerSide && index < totalNodes; y++) {
            for (let x = 0; x < nodesPerSide && index < totalNodes; x++, index++) {
                const posX = x * spacing - (this.globalSize / 2) + (spacing / 2);
                const posY = y * spacing - (this.globalSize / 2) + (spacing / 2);
                const posZ = this.mode3D ? (z * spacing - (this.globalSize / 2) + (spacing / 2)) : 0; // Adjust Z only for 3D

                nodesToArrange[index].x = posX;
                nodesToArrange[index].y = posY;
                nodesToArrange[index].z = posZ;

                // Fix the nodes to their new positions
                nodesToArrange[index].fx = nodesToArrange[index].x;
                nodesToArrange[index].fy = nodesToArrange[index].y;
                nodesToArrange[index].fz = nodesToArrange[index].z;
            }
        }
    }

    this.setGraphData({ ...graphData });
  };
 

  distributeNodesInTriangle(graphData) {
    this.removeChainLabelNodes(graphData);
    const nodes = graphData.nodes;
    const spacing = 300;
    const vspacing = 200;
    //const hspacing = 400;

  
    let levels = 1;
    let nodeIndex = 0;
    // Find the number of levels needed to ensure all nodes fit
    while ((levels * (levels + 1)) / 2 < nodes.length) {
        levels++;
    }

    // Calculate the total height and maximum width for centering
    let totalHeight = (levels - 1) * vspacing; // Assuming '100' is the vertical spacing
    //let maxWidth = (levels - 1) * hspacing; // Assuming '100' is the horizontal spacing

    while (nodeIndex < nodes.length && levels > 0) {
        let currentLevelWidth = (levels - 1) * spacing; // Width of current level
        let xOffset = -currentLevelWidth / 2; // Centering horizontally
        let yOffset = -totalHeight / 2 + ((levels - 1) * spacing); // Centering vertically

        for (let i = 0; i < levels && nodeIndex < nodes.length; i++) {
            // Ensure node exists before setting properties
            if (nodes[nodeIndex]) {
                nodes[nodeIndex].x = xOffset + i * 100; // Place nodes horizontally
                nodes[nodeIndex].y = yOffset; // Place nodes vertically
                nodes[nodeIndex].z = 0; // Flat for 2D

                // Fix positions
                nodes[nodeIndex].fx = nodes[nodeIndex].x;
                nodes[nodeIndex].fy = nodes[nodeIndex].y;
                nodes[nodeIndex].fz = nodes[nodeIndex].z;
            }

            nodeIndex++;
        }
        levels--;  // Decrease the number of nodes for the next level
    }
    this.setGraphData({ ...graphData });
  };


  distributeNodesInPyramid(graphData) {
    this.removeChainLabelNodes(graphData);
    const nodes = graphData.nodes;

    // Calculate the number of levels needed
    let levels = 1;
    let nodesCount = nodes.length;
    // Adjust levels calculation for 3D structure
    while ((levels * (levels + 1) * (2 * levels + 1) / 6) < nodesCount) {
        levels++;
    }

    const spacing = 300; // Adjust based on desired distance between nodes
    let nodeIndex = 0;  // Index to track the node being placed

    // Calculate and distribute nodes for each level
    for (let level = 0; level < levels && nodeIndex < nodesCount; level++) {
        let levelWidth = levels - level;  // Width of this level in the pyramid
        //let rowStartIndex = nodeIndex;  // Keep track of the start index for this row

        for (let row = 0; row < levelWidth && nodeIndex < nodesCount; row++) {
            for (let col = 0; col < levelWidth && nodeIndex < nodesCount; col++) {
                // Calculate the position for each node
                let x = (col - levelWidth / 2 + 0.5) * spacing;
                let y = (levelWidth / 2 - row - 0.5) * spacing;
                let z = this.mode3D ? -level * spacing : 0;  // Move up each level in 3D

                nodes[nodeIndex].x = x;
                nodes[nodeIndex].y = y;
                nodes[nodeIndex].z = z;

                // Fix the nodes to their new positions
                nodes[nodeIndex].fx = nodes[nodeIndex].x;
                nodes[nodeIndex].fy = nodes[nodeIndex].y;
                nodes[nodeIndex].fz = nodes[nodeIndex].z;

                nodeIndex++;  // Move to the next node
            }
        }
    }
    this.setGraphData({ ...graphData });
  };


  distributeNodesInPyramidOrTriangle(graphData) {

    if (this.mode3D) {
      this.distributeNodesInPyramid(graphData);
    }
    else {
      this.distributeNodesInTriangle(graphData);
    }

  };

  
  saveOriginalLocations(nodes) {
    nodes.forEach(node => {
      // Save original positions
      node.originalX = node.fx || node.x;
      node.originalY = node.fy || node.y;
      node.originalZ = node.fz || node.z;
    });
  };
  

  handleDistributeAll(graphData) {
    if (this.mode3D) {
      this.handleDistributeSphereAll(graphData);
    } else {
      this.handleDistributeCircleAll(graphData);
    }
  }

  handleDistributeCircleAll(graphData) {
    this.removeChainLabelNodes(graphData);
    // Check if in organic layout (nodes have nodeType property)
    const areNodesClassified = graphData.nodes.some(node => node.nodeType);
    
    // Determine which nodes are actually visible based on the current view mode
    let visibleNodes;
    
    if (areNodesClassified) {
      // Get the current view mode with safeguards
      const viewMode = (typeof this.viewMode !== 'undefined' && this.viewMode !== null) ? this.viewMode : 'all';
      
      // Filter nodes based on the view mode
      if (viewMode === 'all') {
        // All nodes are visible
        visibleNodes = graphData.nodes;
      } 
      else if (viewMode === 'token_connected') {
        // Only tokens and users connected to tokens are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' || node.nodeType === 'connected_user'
        );
      } 
      else if (viewMode === 'users_only') {
        // Only users (both connected and isolated) are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'connected_user' || node.nodeType === 'isolated_user'
        );
      }
      else if (viewMode === 'tokens_only') {
        // Only tokens with custom icons are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' && node.icon && node.icon !== genericTokenIcon
        );
      }
      else {
        // Default case: use all nodes
        visibleNodes = graphData.nodes;
      }
      
      console.log(`Circle layout: distributing ${visibleNodes.length} nodes out of ${graphData.nodes.length} total nodes with view mode: ${viewMode}`);
      const radius = this.getAdaptiveRadius(visibleNodes.length);
      
      // Distribute visible nodes in a circle
      this.distributeIconsOnCircle(graphData, visibleNodes, radius);
    } else {
      // Default behavior for non-organic layout
      this.handleDistributeCircleUsers(graphData);
      this.handleDistributeCircleTokens(graphData);
    }
    
    this.setGraphData({ ...graphData });
  };

  getGraphCenter(nodes) {
    const total = nodes.reduce(
      (acc, node) => {
        acc.x += node.x || 0;
        acc.y += node.y || 0;
        acc.z += node.z || 0;
        return acc;
      },
      { x: 0, y: 0, z: 0 }
    );
  
    const count = nodes.length;
    return {
      x: total.x / count,
      y: total.y / count,
      z: total.z / count
    };
  };


  getAdaptiveRadius(n) {
    if (n < 50) {
      return 600 + Math.pow(n, 1.1);
    } else if (n < 200) {
      return 700 + Math.sqrt(n) * 20;
    } else if (n < 500) {
      return 1000 + Math.log(n) * 60; // NEW mid-large case
    } else {
      return 1500 + Math.log(n) * 75;
    }
  };
  
  
   distributeIconsAsLine(graphData) {
    const nodes = graphData.nodes;
    const totalNodes = nodes.length;
    const gap = 200; // This defines the spacing between each node on the line

    // Starting point for the first node, centers the middle node
    const startX = -gap * (totalNodes - 1) / 2;

    for (let i = 0; i < totalNodes; i++) {
        // Each node's x position is increased by 'gap' units from the starting x position
        nodes[i].x = startX + i * gap;
        nodes[i].y = 0; // All nodes at the same y position for a straight line
        nodes[i].z = 0; // Assuming a 2D layout for simplicity

        // Fix the nodes to their new positions
        nodes[i].fx = nodes[i].x;
        nodes[i].fy = nodes[i].y;
        nodes[i].fz = nodes[i].z; // If 3D is needed, adjust z accordingly
    }

    this.setGraphData({ ...graphData, nodes: [...nodes] });
  };


  distributeIconsOnSphere(graphData, nodes, radius) {
    const totalNodes = nodes.length;
    if (totalNodes === 0) return; // Don't proceed if no nodes to distribute
    
    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians

    for (let i = 0; i < totalNodes; i++) {
        const y = 1 - (i / (totalNodes > 1 ? totalNodes - 1 : 1)) * 2; // y goes from 1 to -1, avoid division by zero
        const radiusAtY = Math.sqrt(1 - y * y); // radius at y

        const theta = phi * i; // golden angle increment

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        nodes[i].x = radius * x;
        nodes[i].y = radius * y;
        nodes[i].z = (this.mode3D) ? (radius * z) : this.globalDistance;

        // Fix the nodes to their new positions
        nodes[i].fx = nodes[i].x;
        nodes[i].fy = nodes[i].y;
        nodes[i].fz = nodes[i].z;
    }
    
    // Only update the graph data if we're not called by another function that will update it
    // This avoids unnecessary renders
    if (nodes === graphData.nodes) {
        this.setGraphData({ ...graphData });
    }
  };

  // Variant of distributeIconsOnSphere that positions a group of nodes on a
  // sphere centered at the provided offset. This lets us create separate
  // spheres for different logical groups (e.g., chains) in 3D space.
  distributeIconsOnSphereAtCenter(graphData, nodes, radius, center) {
    const totalNodes = nodes.length;
    if (totalNodes === 0) return;

    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians

    for (let i = 0; i < totalNodes; i++) {
      const y = 1 - (i / (totalNodes > 1 ? totalNodes - 1 : 1)) * 2; // y goes from 1 to -1
      const radiusAtY = Math.sqrt(1 - y * y); // radius at y

      const theta = phi * i; // golden angle increment

      const x = Math.cos(theta) * radiusAtY;
      const z = Math.sin(theta) * radiusAtY;

      const baseX = radius * x;
      const baseY = radius * y;
      const baseZ = (this.mode3D) ? (radius * z) : this.globalDistance;

      nodes[i].x = (center?.x || 0) + baseX;
      nodes[i].y = (center?.y || 0) + baseY;
      nodes[i].z = (center?.z || 0) + baseZ;

      // Fix the nodes to their new positions
      nodes[i].fx = nodes[i].x;
      nodes[i].fy = nodes[i].y;
      nodes[i].fz = nodes[i].z;
    }
  };

  distributeIconsOnCircle(graphData, nodes, radius) {
    const totalNodes = nodes.length;
    if (totalNodes === 0) return; // Don't proceed if no nodes to distribute
    
    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians for even distribution

    for (let i = 0; i < totalNodes; i++) {
      const theta = phi * i; // golden angle increment
  
      const x = Math.cos(theta);
      const y = Math.sin(theta);
      const z = Math.sin(theta);
  
      nodes[i].x = radius * x;
      nodes[i].y = radius * y;
      nodes[i].z = (this.mode3D) ? z : this.globalDistance;
  
      // Fix the nodes to their new positions
      nodes[i].fx = nodes[i].x;
      nodes[i].fy = nodes[i].y;
      nodes[i].fz = z;
    }

    // Only update the graph data if we're not called by another function that will update it
    if (nodes === graphData.nodes) {
      this.setGraphData({ ...graphData });
    }
  };
  

  distributeIcons(graphData, nodes, radius) {
    if (this.mode3D) {
      console.log('GraphLayoutManager: distributeIconsOnSphere (generic)', {
        nodeCount: nodes.length,
      });
      this.distributeIconsOnSphere(graphData, nodes, radius);
    } 
    else {
      this.distributeIconsOnCircle(graphData, nodes, radius);
    }       
  }

  
  handleDistributeSphereAll(graphData) {
    console.log('GraphLayoutManager: handleDistributeSphereAll (chain layout)', {
      nodeCount: graphData.nodes.length,
    });
    // Check if nodes have nodeType property which indicates they've been classified
    // This means we're in organic layout mode and should only distribute visible nodes
    const areNodesClassified = graphData.nodes.some(node => node.nodeType);
    
    // Determine which nodes are actually visible based on the current view mode
    // This information is stored in the nodeType property set by classifyGraphNodes
    let visibleNodes;
    
    if (areNodesClassified) {
      // Get the current view mode from App.js via the property if available
      // Safeguard against undefined viewMode by defaulting to 'all'
      const viewMode = (typeof this.viewMode !== 'undefined' && this.viewMode !== null) ? this.viewMode : 'all';
      
      // Filter nodes based on the view mode
      if (viewMode === 'all') {
        // All nodes are visible
        visibleNodes = graphData.nodes;
      } 
      else if (viewMode === 'token_connected') {
        // Only tokens and users connected to tokens are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' || node.nodeType === 'connected_user'
        );
      } 
      else if (viewMode === 'users_only') {
        // Only users (both connected and isolated) are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'connected_user' || node.nodeType === 'isolated_user'
        );
      }
      else if (viewMode === 'tokens_only') {
        // Only tokens with custom icons are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' && node.icon && node.icon !== genericTokenIcon
        );
      }
      else {
        // Default case: use all nodes
        visibleNodes = graphData.nodes;
      }
    } else {
      // If not in organic layout mode, distribute all nodes
      visibleNodes = graphData.nodes;
    }
    
    console.log(`Distributing ${visibleNodes.length} nodes out of ${graphData.nodes.length} total nodes with view mode: ${this.viewMode || 'all'} and tokenView: ${this.tokenView}`);

    // If we are not in 3D mode, or if we are not in chain token view,
    // fall back to the single-sphere behavior with no synthetic chain
    // label nodes.
    if (!this.mode3D || this.tokenView !== 'chain') {
      const radius = this.getAdaptiveRadius(visibleNodes.length);
      this.distributeIcons(graphData, visibleNodes, radius);
      this.setGraphData({ ...graphData });
    }
    else {
      // Group visible token nodes by chain so each chain can occupy its own
      // sphere in 3D space. Non-token or chain-less nodes are placed in a
      // central sphere as a fallback.
      // Also clear out any previous synthetic chain label nodes so we don't
      // accumulate duplicates on repeated layouts.
      graphData.nodes = graphData.nodes.filter(n => n.nodeType !== 'chain_label');

      const tokensByChain = new Map();
      const otherNodes = [];

      visibleNodes.forEach(node => {
        if (node.nodeType === 'token' && node.chain) {
          const key = String(node.chain).toUpperCase();
          if (!tokensByChain.has(key)) {
            tokensByChain.set(key, []);
          }
          tokensByChain.get(key).push(node);
        } else {
          otherNodes.push(node);
        }
      });

      // If we ended up with no chain-grouped tokens, keep the existing behavior.
      if (tokensByChain.size === 0) {
        const radius = this.getAdaptiveRadius(visibleNodes.length);
        this.distributeIcons(graphData, visibleNodes, radius);
        this.setGraphData({ ...graphData });
      }
      else {
        // Base radius for individual chain spheres, scaled from total visible
        // nodes so the overall scene stays roughly comparable in size.
        const baseRadius = this.getAdaptiveRadius(visibleNodes.length) * 0.6;

        // Define preferred order and positions for known chains so layouts stay
        // stable between runs.
        const preferredChainOrder = ['ETH', 'ARB', 'OPT', 'BSC', 'POL', 'AVAX'];
        const orderedChains = [];

        preferredChainOrder.forEach(key => {
          if (tokensByChain.has(key)) {
            orderedChains.push(key);
          }
        });

        // Append any remaining chains not in the preferred list.
        tokensByChain.forEach((_, key) => {
          if (!preferredChainOrder.includes(key)) {
            orderedChains.push(key);
          }
        });

        const chainCount = orderedChains.length;
        const ringRadius = baseRadius * 3.0;

        // Match NodeLists.js chain color palette for visual consistency.
        const chainColors = {
          ETH: 0x3f51b5,
          ARB: 0x00bcd4,
          OPT: 0xe53935,
          BSC: 0xfbc02d,
          POL: 0x9c27b0,
          AVAX: 0xef6c00,
        };

        // Place each chain on its own sphere around a big ring that is slightly
        // tilted out of the XZ plane. This inherent tilt gives a stronger 3D
        // feel even if the camera is relatively level.
        const ringTiltAngle = Math.PI / 6; // 30° tilt around the X axis

        orderedChains.forEach((chainKey, index) => {
          const nodesForChain = tokensByChain.get(chainKey) || [];
          if (!nodesForChain.length) return;

          const angle = (index / Math.max(chainCount, 1)) * Math.PI * 2;
          const baseX = ringRadius * Math.cos(angle);
          const baseY = 0;
          const baseZ = ringRadius * Math.sin(angle);

          // Rotate the ring around the X axis by ringTiltAngle so some chain
          // spheres sit "above" and some "below" the center line, and their
          // depths differ even before any camera lean.
          const rotatedY = baseY * Math.cos(ringTiltAngle) - baseZ * Math.sin(ringTiltAngle);
          const rotatedZ = baseY * Math.sin(ringTiltAngle) + baseZ * Math.cos(ringTiltAngle);

          const center = {
            x: baseX,
            y: rotatedY,
            z: rotatedZ,
          };

          this.distributeIconsOnSphereAtCenter(graphData, nodesForChain, baseRadius, center);

          // Add a synthetic label node at the center of this chain sphere so the
          // renderer can draw a large semi-transparent chain label.
          const color = chainColors[chainKey] ?? 0x555555;
          graphData.nodes.push({
            id: `CHAIN_LABEL_${chainKey}`,
            label: chainKey,
            chain: chainKey,
            nodeType: 'chain_label',
            color,
            labelRadius: baseRadius,
            x: center.x,
            y: center.y,
            z: center.z,
            fx: center.x,
            fy: center.y,
            fz: center.z,
          });
        });

        // Place all remaining nodes (users, unlabeled, etc.) in a central sphere
        // so they remain visible but do not collide with chain clusters.
        if (otherNodes.length > 0) {
          const centerRadius = baseRadius * 0.7;
          const center = { x: 0, y: 0, z: 0 };
          this.distributeIconsOnSphereAtCenter(graphData, otherNodes, centerRadius, center);
        }

        // Commit the updated layout and auto-fit the camera so all chain spheres
        // are comfortably framed in view. Because the ForceGraph instance may not
        // yet be ready when this runs, wait until graphRef.current and
        // cameraPosition are available before calling autoFit.
        this.setGraphData({ ...graphData });
      }
    }

    const MAX_ATTEMPTS = 15;
    const RETRY_DELAY_MS = 200;

    const scheduleChainAutoFit = (attempt = 0) => {
      if (
        !this.graphRef?.current ||
        typeof this.graphRef.current.cameraPosition !== 'function'
      ) {
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => scheduleChainAutoFit(attempt + 1), RETRY_DELAY_MS);
        }
        return;
      }

      // Use a stronger distance multiplier than the default so chain mode
      // appears more zoomed out and all spheres are clearly visible.
      this.autoFit(graphData, 1.0);

      // Only start the camera spin after we've successfully zoomed out for
      // chain mode, so the spin doesn't fight with the framing. Delay this
      // until after the autoFit camera transition (~1500ms) has mostly
      // completed so the framing is stable before the spin kicks in.
      //if (this.spinCamera && typeof this.startCameraSpin === 'function') {
        setTimeout(() => {
          this.startCameraSpin();
        }, 1600);
      //}
    };

    scheduleChainAutoFit();
  };
  

  handleDistributeUsers(graphData) {
    const nodesWithoutIcons = graphData.nodes.filter(node => node.icon === '');
    this.distributeIcons(graphData, nodesWithoutIcons, graphData.nodes.length ); 
    this.setGraphData({ ...graphData });
  }


  handleExpandGraph(graphData, expandTimes = 1) {
    const center = { x: 0, y: 0, z: 0 };
    const expansionFactor = 2.0; // base expansion factor per pass
    const maxRadius = 50000; // hard cap to prevent Infinity/NaN in Three geometries
    const minScale = 0.5;
    const maxScale = 5;
    const skipPinAndScale = this.dataSource === PCAP && !!this.pcapGroupEnabled;

    // In PCAP grouped mode, ForceGraph mutates a cloned graphData for rendering,
    // so the app-state graphData passed into this function may not have x/y/z yet.
    // If we scale undefined coords (defaulting to 0), everything collapses.
    // Seed missing coords deterministically so expand/compress behaves sensibly.
    const seedMissingCoords = (nodes) => {
      const baseR = 80;
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        if (!n) continue;
        const xOk = Number.isFinite(Number(n.x));
        const yOk = Number.isFinite(Number(n.y));
        const zOk = Number.isFinite(Number(n.z));
        if (xOk && yOk && (this.mode3D ? zOk : true)) continue;

        const a = i * golden;
        const r = baseR * Math.sqrt((i + 1) / (nodes.length + 1));
        n.x = Number.isFinite(Number(n.x)) ? n.x : r * Math.cos(a);
        n.y = Number.isFinite(Number(n.y)) ? n.y : r * Math.sin(a);
        n.z = this.mode3D ? (Number.isFinite(Number(n.z)) ? n.z : (r * 0.15) * Math.sin(a * 0.7)) : 0;
      }
    };

    const finiteOr = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    seedMissingCoords(graphData.nodes || []);

    // Apply the expansion multiple times if requested
    for (let i = 0; i < expandTimes; i++) {
      graphData.nodes.forEach(node => {

          const x0 = finiteOr(node.x, 0);
          const y0 = finiteOr(node.y, 0);
          const z0 = finiteOr(node.z, 0);

          let x = center.x + (x0 - center.x) * expansionFactor;
          let y = center.y + (y0 - center.y) * expansionFactor;
          let z = center.z + (z0 - center.z) * expansionFactor;

          // Clamp to a maximum radius to avoid runaway growth.
          const dx = x - center.x;
          const dy = y - center.y;
          const dz = z - center.z;
          const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (Number.isFinite(r) && r > maxRadius) {
            const s = maxRadius / r;
            x = center.x + dx * s;
            y = center.y + dy * s;
            z = center.z + dz * s;
          }

          node.x = finiteOr(x, 0);
          node.y = finiteOr(y, 0);
          node.z = finiteOr(z, 0);
  
          // Expand/Compress are intended to act like a deterministic scale
          // transform (like other views). Pin nodes to the new positions so a
          // reheated simulation does not relax back to the same equilibrium.
          node.fx = node.x;
          node.fy = node.y;
          node.fz = node.z;

        if (!skipPinAndScale && node.icon) {
          // Scale node size multiplicatively so compress can approximately invert it
          const currentScale = node.expandScale || 1;
          let nextScale = currentScale * expansionFactor;
          if (nextScale > maxScale) nextScale = maxScale;
          if (nextScale < minScale) nextScale = minScale;
          node.expandScale = nextScale;
        }
      });
    }

    this.setGraphData({ ...graphData });
  }


  handleCompressGraph(graphData, compressTimes=1) {
    const center = { x: 0, y: 0, z: 0 };
    const expansionFactor = 2.0; // keep in sync with handleExpandGraph
    const compressionFactor = 1 / expansionFactor; // less than 1 compresses toward center
    const minDistance = 0; // minimum distance from center after compression
    const minScale = 0.5;
    const maxScale = 5;
    const skipPinAndScale = this.dataSource === PCAP && !!this.pcapGroupEnabled;

    const seedMissingCoords = (nodes) => {
      const baseR = 80;
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        if (!n) continue;
        const xOk = Number.isFinite(Number(n.x));
        const yOk = Number.isFinite(Number(n.y));
        const zOk = Number.isFinite(Number(n.z));
        if (xOk && yOk && (this.mode3D ? zOk : true)) continue;

        const a = i * golden;
        const r = baseR * Math.sqrt((i + 1) / (nodes.length + 1));
        n.x = Number.isFinite(Number(n.x)) ? n.x : r * Math.cos(a);
        n.y = Number.isFinite(Number(n.y)) ? n.y : r * Math.sin(a);
        n.z = this.mode3D ? (Number.isFinite(Number(n.z)) ? n.z : (r * 0.15) * Math.sin(a * 0.7)) : 0;
      }
    };

    const finiteOr = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    seedMissingCoords(graphData.nodes || []);

    for (let i = 0; i < compressTimes; i++) {
      graphData.nodes.forEach(node => {  
          const x0 = finiteOr(node.x, 0);
          const y0 = finiteOr(node.y, 0);
          const z0 = finiteOr(node.z, 0);
          const newX = center.x + (x0 - center.x) * compressionFactor;
          const newY = center.y + (y0 - center.y) * compressionFactor;
          const newZ = center.z + (z0 - center.z) * compressionFactor;

          const distance = Math.sqrt(
            Math.pow(newX - center.x, 2) +
            Math.pow(newY - center.y, 2) +
            Math.pow(newZ - center.z, 2)
          );

          // Only apply compression if we stay above the minimum distance
          if (Number.isFinite(distance) && distance > minDistance) {
            node.x = finiteOr(newX, 0);
            node.y = finiteOr(newY, 0);
            node.z = finiteOr(newZ, 0);
          }

          // Expand/Compress are intended to act like a deterministic scale
          // transform (like other views). Pin nodes to the new positions so a
          // reheated simulation does not relax back to the same equilibrium.
          node.fx = node.x;
          node.fy = node.y;
          node.fz = node.z;

        if (!skipPinAndScale && node.icon) {
          // Opposite of expand: shrink visual scale, but never below 0.5
          const currentScale = node.expandScale || 1;
          let nextScale = currentScale / expansionFactor;
          if (nextScale > maxScale) nextScale = maxScale;
          if (nextScale < minScale) nextScale = minScale;
          node.expandScale = nextScale;
        }
      });
    }

    this.setGraphData({ ...graphData });

    if (skipPinAndScale && this.graphRef && this.graphRef.current && typeof this.graphRef.current.d3ReheatSimulation === 'function') {
      try {
        //this.graphRef.current.d3ReheatSimulation();
      } catch (e) {
      }
    }
  }

  resetToSimulationLayout(graphData) {
    if (!graphData || !graphData.nodes || !graphData.nodes.length) {
      return;
    }
    // Strip out synthetic chain label nodes (big colored spheres) so the
    // organic layout only shows real graph nodes.
    this.removeChainLabelNodes(graphData);

    // Unpin nodes so the force simulation is free to position them
    // organically. We deliberately avoid mutating velocities or x/y/z
    // here to keep the graph data consistent; the fresh ForceGraph
    // instance created by remounting will restart its internal
    // simulation from the current positions.
    graphData.nodes.forEach(node => {
      if (!node) return;
      const radius = this.globalDistance || 500;
      const r = radius * 0.1;
      const rx = (Math.random() - 0.5) * r;
      const ry = (Math.random() - 0.5) * r;
      const rz = this.mode3D ? (Math.random() - 0.5) * r : 0;

      node.x = rx;
      node.y = ry;
      node.z = rz;
      node.fx = undefined;
      node.fy = undefined;
      node.fz = undefined;
      node.vx = 0;
      node.vy = 0;
      node.vz = 0;
    });

    this.graphRef.current.pauseAnimation();

    this.setGraphData({ ...graphData });

    // Force a full unmount/remount of the ForceGraph instance so we get a
    // brand-new internal simulation, similar to loading /links from
    // scratch.
    requestAnimationFrame(() => {
        this.setGraphInstanceKey(prev => (prev + 1));
    });

    //this.graphRef.current.d3ReheatSimulation();
    //this.graphRef.current.resumeAnimation();

  }
  
  handleDistributeTokens(graphData) {
    const nodesWithIcons = graphData.nodes.filter(node => node.icon !== '');
    this.distributeIcons(graphData, nodesWithIcons, graphData.nodes.length + 300);
    this.setGraphData({ ...graphData });
  }


  handleDistributeUndo(graphData) {
    // Reset to original locations
    const nodes = graphData.nodes;
    nodes.forEach(node => {
      // Reset positions
      node.fx = node.originalX;
      node.fy = node.originalY;
      node.fz = node.originalZ;
    });
    this.setGraphData({ ...graphData });
  };

  
  handleDistributeCircleUsers(graphData) {
    const nodesWithoutIcons = graphData.nodes.filter(node => node.icon === '');
    this.distributeIconsOnCircle(graphData, nodesWithoutIcons, 1500);
    this.setGraphData({ ...graphData });
  };
  

  handleDistributeCircleTokens(graphData) {
    const nodesWithIcons = graphData.nodes.filter(node => node.icon !== '');
    this.distributeIconsOnCircle(graphData, nodesWithIcons, 2000);
    this.setGraphData({ ...graphData });
  };
  
  
  distributeUsersAroundToken(graphData, token) {
    const activeNode = (token) ? token : ( this.activeNodeId) ? graphData.nodes.find(n => n.id === this.activeNodeId) : null;
    if (!activeNode) return;

    // Find the connected user nodes by link references
    const connectedNodeIds = new Set();
    graphData.links.forEach(link => {
      if (link.source.id === activeNode.id) {
        connectedNodeIds.add(link.target.id);
      } else if (link.target.id === activeNode.id) {
        connectedNodeIds.add(link.source.id);
      }
    });

    // Get the actual node objects for users
    const connectedNodes = graphData.nodes.filter(node => connectedNodeIds.has(node.id));
    if (!connectedNodes.length) return;

    // Adjust the radius based on the number of connected nodes
    const baseRadius = 200; // Starting radius
    const incrementPerNode = 1.5; // Increment the radius by this amount for each node
    const radius = baseRadius + (incrementPerNode * connectedNodes.length);

    if (this.mode3D) {
      // Distribute nodes on a sphere
      const totalNodes = connectedNodes.length;
      const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians

      for (let i = 0; i < totalNodes; i++) {
        const y = 1 - (i / (totalNodes - 1)) * 2; // y goes from 1 to -1
        const radiusAtY = Math.sqrt(1 - y * y); // radius at y

        const theta = phi * i; // golden angle increment

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        connectedNodes[i].x = activeNode.x + radius * x;
        connectedNodes[i].y = activeNode.y + radius * y;
        connectedNodes[i].z = activeNode.z + radius * z;

        // Fix the nodes to their new positions
        connectedNodes[i].fx = connectedNodes[i].x;
        connectedNodes[i].fy = connectedNodes[i].y;
        connectedNodes[i].fz = connectedNodes[i].z;
      }
    } else {
      // Distribute nodes on a circle
      const angleStep = (2 * Math.PI) / connectedNodes.length;

      connectedNodes.forEach((node, index) => {
        const angle = index * angleStep;
        node.x = activeNode.x + radius * Math.cos(angle);
        node.y = activeNode.y + radius * Math.sin(angle);

        // For 2D, keep the z value as the active node or set to a fixed value
        node.z = activeNode.z;

        node.fx = node.x;
        node.fy = node.y;
        node.fz = node.z;
      });
    }

    if (!token) {
      this.setGraphData({ ...graphData });
    }
  };

  // New function to distribute users around all tokens
  distributeUsersAroundAllTokens(graphData) {
    this.handleDistributeLock(graphData); // lock the positions first
    const tokenNodes = graphData.nodes.filter(node => node.label ? !node.label.startsWith('0x') : false); 

    tokenNodes.forEach(tokenNode => {
      this.distributeUsersAroundToken(graphData, tokenNode);
    });

    this.setGraphData({ ...graphData }); // Update graph data at the end
  };


  distributeConnectedNodesAroundTube(graphData) {
    // Tube mode should never show synthetic chain label nodes from the
    // chain-sphere layout, so strip them first.
    this.removeChainLabelNodes(graphData);

    // Check if nodes have nodeType property which indicates they've been classified
    const areNodesClassified = graphData.nodes.some(node => node.nodeType);
    
    // Determine which nodes are actually visible based on the current view mode
    let visibleNodes = [];
    
    if (areNodesClassified) {
      // Get the current view mode with safeguards
      const viewMode = (typeof this.viewMode !== 'undefined' && this.viewMode !== null) ? this.viewMode : 'all';
      
      // Filter nodes based on the view mode
      if (viewMode === 'all') {
        // All nodes are visible
        visibleNodes = [...graphData.nodes]; // Create a copy to avoid direct modification
      } 
      else if (viewMode === 'token_connected') {
        // Only tokens and users connected to tokens are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' || node.nodeType === 'connected_user'
        );
      } 
      else if (viewMode === 'users_only') {
        // Only users (both connected and isolated) are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'connected_user' || node.nodeType === 'isolated_user'
        );
      }
      else if (viewMode === 'tokens_only') {
        // Only tokens with custom icons are visible
        visibleNodes = graphData.nodes.filter(node => 
          node.nodeType === 'token' && node.icon && node.icon !== genericTokenIcon
        );
      }
      else {
        // Default case: use all nodes
        visibleNodes = [...graphData.nodes];
      }
      
      console.log(`Tube layout: distributing ${visibleNodes.length} nodes out of ${graphData.nodes.length} total nodes with view mode: ${viewMode}`);
    } else {
      // If not in organic layout mode, use all nodes
      visibleNodes = [...graphData.nodes];
    }
    
    // First place nodes in a grid pattern to give them initial positions
    const totalNodes = visibleNodes.length;
    const gridSize = 500; // Size of the grid
    const gridSpacing = gridSize / Math.sqrt(totalNodes);
    
    for (let i = 0; i < totalNodes; i++) {
      const gridX = Math.floor(i % Math.sqrt(totalNodes));
      const gridY = Math.floor(i / Math.sqrt(totalNodes));
      
      visibleNodes[i].x = gridX * gridSpacing - gridSize/2;
      visibleNodes[i].y = gridY * gridSpacing - gridSize/2;
      visibleNodes[i].z = 0;
      
      // Fix positions
      visibleNodes[i].fx = visibleNodes[i].x;
      visibleNodes[i].fy = visibleNodes[i].y;
      visibleNodes[i].fz = visibleNodes[i].z;
    }
    
    // Define the layout parameters for the tube
    const numRings = 7; // More rings for a wider spread
    const ringSpacing = 450; // Much larger spacing between rings
    const ringRadius = 550; // Much larger radius for all rings
    const nodesPerRing = Math.ceil(totalNodes / numRings);
    
    // Place nodes in rings
    for (let ring = 0; ring < numRings; ring++) {
      const startIndex = ring * nodesPerRing;
      const endIndex = Math.min(startIndex + nodesPerRing, totalNodes);
      const nodesInCurrentRing = endIndex - startIndex;
      
      if (nodesInCurrentRing <= 0) continue;
      
      // Calculate the x-offset for this ring to create the tube shape
      // Evenly space the rings along the x-axis
      const xOffset = ring * ringSpacing - (ringSpacing * (numRings - 1) / 2);
      
      // Distribute nodes evenly around this ring
      for (let i = 0; i < nodesInCurrentRing; i++) {
        const nodeIndex = startIndex + i;
        if (nodeIndex >= totalNodes) break;
        
        // Calculate angle for even distribution
        const angle = (i / nodesInCurrentRing) * Math.PI * 2;
        
        // Position on the ring with consistent radius
        visibleNodes[nodeIndex].x = xOffset;
        visibleNodes[nodeIndex].y = ringRadius * Math.cos(angle);
        visibleNodes[nodeIndex].z = ringRadius * Math.sin(angle);
        
        // Fix the positions
        visibleNodes[nodeIndex].fx = visibleNodes[nodeIndex].x;
        visibleNodes[nodeIndex].fy = visibleNodes[nodeIndex].y;
        visibleNodes[nodeIndex].fz = visibleNodes[nodeIndex].z;
      }
    }
    
    // Apply a final expansion to make the entire formation larger
    const expansionFactor = 1.5;
    const center = { x: 0, y: 0, z: 0 };
    
    for (const node of visibleNodes) {
      // Move the node position further away from the center
      node.x = center.x + (node.x - center.x) * expansionFactor;
      node.y = center.y + (node.y - center.y) * expansionFactor;
      node.z = center.z + (node.z - center.z) * expansionFactor;
      
      // Fix the positions
      node.fx = node.x;
      node.fy = node.y;
      node.fz = node.z;
    }
    
    // Apply final graph update
    this.setGraphData({ ...graphData });
  };
  

  handleDistributeLock(graphData) {
    graphData.nodes.forEach(node => {
      node.fx = node.x;
      node.fy = node.y;
      node.fz = node.z;
    });
  }

  zoomOut(howMuch = 1000) {  
    // Camera position manipulation only works in 3D mode
    if (!this.is3DMode || !this.graphRef || !this.graphRef.current || !this.graphRef.current.cameraPosition) {
      // For 2D mode, use zoom method if available
      if (!this.is3DMode && this.graphRef?.current?.zoom) {
        const currentZoom = this.graphRef.current.zoom();
        this.graphRef.current.zoom(currentZoom * 0.8, 300); // Zoom out by 20%
      }
      return;
    }
    
    var spinCamera2 = this.spinCamera;
    if (spinCamera2) {
      this.stopCameraSpin();
    }
    const currentPosition = this.graphRef.current.cameraPosition();
    this.graphRef.current.cameraPosition(
      { x: currentPosition.x, y: currentPosition.y, z: currentPosition.z + howMuch }, // Increase z value to zoom out
      { x: 0, y: 0, z: 0 }, // Look at the center of the graph
      0 // Transition duration
    );  
    if (spinCamera2) {
      this.startCameraSpin();
    } 
  };

  zoomIn(howMuch = 1000) { 
    // Camera position manipulation only works in 3D mode
    if (!this.is3DMode || !this.graphRef || !this.graphRef.current || !this.graphRef.current.cameraPosition) {
      // For 2D mode, use zoom method if available
      if (!this.is3DMode && this.graphRef?.current?.zoom) {
        const currentZoom = this.graphRef.current.zoom();
        this.graphRef.current.zoom(currentZoom * 1.25, 300); // Zoom in by 25%
      }
      return;
    }
    
    var spinCamera2 = this.spinCamera;
    if (spinCamera2) {
      this.stopCameraSpin();
    }
    const currentPosition = this.graphRef.current.cameraPosition();
    let newZ = currentPosition.z - howMuch;
    if (newZ < 0) {
      newZ = 0;
    }
    this.graphRef.current.cameraPosition(
      { x: currentPosition.x, y: currentPosition.y, z: newZ }, // Increase z value to zoom out
      { x: 0, y: 0, z: 0 }, // Look at the center of the graph
      0 // Transition duration
    );
    if (spinCamera2) {
      this.startCameraSpin();
    } 
  };

  handleZoomOut(graphData) {
    const { nodes } = graphData; // Assuming graphData is in the state or props

    if (nodes.length === 0) {
      return; // No nodes to calculate bounds
    }

    const graphNodes = nodes;
    if (graphNodes.length === 0) {
      return; // No nodes to calculate bounds
    }
  
    // Calculate the bounds of the nodes
    let maxX = Number.MIN_SAFE_INTEGER, maxY = Number.MIN_SAFE_INTEGER, maxZ = Number.MIN_SAFE_INTEGER;
    let minX = Number.MAX_SAFE_INTEGER, minY = Number.MAX_SAFE_INTEGER, minZ = Number.MAX_SAFE_INTEGER;
  
    graphNodes.forEach(node => {
      maxX = Math.max(node.x, maxX);
      maxY = Math.max(node.y, maxY);
      maxZ = Math.max(node.z, maxZ);
      minX = Math.min(node.x, minX);
      minY = Math.min(node.y, minY);
      minZ = Math.min(node.z, minZ);
    });
  
    // Calculate the center point of all nodes
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
  
    // Handle differently for 2D and 3D modes
    if (this.is3DMode && this.graphRef?.current?.cameraPosition) {
      // Calculate the distance needed to view all nodes in 3D
      const distance = (Math.sqrt(Math.pow(maxX - minX, 2) + Math.pow(maxY - minY, 2) + Math.pow(maxZ - minZ, 2))) / 2;
      
      // Set the camera position to include all nodes
      this.graphRef.current.cameraPosition(
        { x: centerX, y: centerY, z: distance * 1.2 }, // Zoom out a bit more than the max distance
        { x: centerX, y: centerY, z: centerZ }, // Look at the center of all nodes
        2000 // ms transition duration
      );
    } else if (!this.is3DMode && this.graphRef?.current) {
      // For 2D mode, center on all nodes and zoom to fit
      if (this.graphRef.current.centerAt) {
        this.graphRef.current.centerAt(centerX, centerY, 1000);
      }
      if (this.graphRef.current.zoomToFit) {
        setTimeout(() => {
          this.graphRef.current.zoomToFit(400, 10);
        }, 200);
      }
    }
  };
  

  // Auto-fit all nodes in the view. In 3D mode, distanceMultiplier controls
  // how far back the camera sits relative to the node bounds when we fall
  // back to manual camera positioning.
  autoFit(graphData, distanceMultiplier = 1.5) {
    if (!this.graphRef?.current || !graphData?.nodes || graphData.nodes.length === 0) {
      return;
    }

    if (!this.is3DMode) {
      // For 2D mode, use a simpler two-stage zoom approach driven by
      // ForceGraph's built-in zoomToFit helper.
      if (this.graphRef.current.zoomToFit) {
        // First zoom with large padding
        this.graphRef.current.zoomToFit(200, 300);

        // Second adjustment after nodes have settled
        setTimeout(() => {
          this.graphRef.current.zoomToFit(300, 150);
        }, 500);
      }
    } else {
      // For 3D mode, prefer the library's zoomToFit if available so the
      // viewport size and perspective are taken into account.
      if (this.graphRef.current.zoomToFit) {
        const nodes = graphData.nodes;
        const nodeCount = nodes.length;
        const iconNodes = nodes.filter(n => n && n.icon).length;
        const iconHeavy = iconNodes > 0 && iconNodes >= nodeCount / 2;

        // When we have a tiny, icon-heavy graph (e.g., a single big token
        // with one or two links), zoomToFit computes bounds from the small
        // node coordinates and moves the camera too close. That puts the
        // camera *inside* the large token icon. In that case, back the
        // camera off to a fixed distance around the graph center instead of
        // relying on zoomToFit.
        if (iconHeavy && nodeCount <= 3 && this.graphRef.current.cameraPosition) {
          const center = this.getGraphCenter(nodes);
          const baseDistance = (this.globalDistance || 800);
          const distance = baseDistance * 2.0;

          this.graphRef.current.cameraPosition(
            { x: center.x, y: center.y, z: distance },
            { x: center.x, y: center.y, z: center.z },
            600
          );
        } else {
          // For normal-sized graphs, zoomToFit works well as long as we use
          // some padding so the layout is comfortably framed.
          this.graphRef.current.zoomToFit(350, 80);
        }

        return;
      }

      // Fallback: manual bounds-based camera positioning if zoomToFit is
      // not available on this graph ref.
      // Calculate bounds of all nodes
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;
      let minZ = Infinity, maxZ = -Infinity;
      
      graphData.nodes.forEach(node => {
        if (node.x < minX) minX = node.x;
        if (node.x > maxX) maxX = node.x;
        if (node.y < minY) minY = node.y;
        if (node.y > maxY) maxY = node.y;
        if (node.z < minZ) minZ = node.z;
        if (node.z > maxZ) maxZ = node.z;
      });
      
      // Calculate center and distance
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const centerZ = (minZ + maxZ) / 2;
      
      // Calculate distance needed to view all nodes
      const distance = Math.max(
        Math.abs(maxX - minX),
        Math.abs(maxY - minY),
        Math.abs(maxZ - minZ)
      ) * distanceMultiplier; // Multiplier to ensure all nodes are visible
      
      // Set camera position in a single smooth transition
      if (this.graphRef.current.cameraPosition) {
        this.graphRef.current.cameraPosition(
          { x: centerX, y: centerY, z: distance }, 
          { x: centerX, y: centerY, z: centerZ },
          1500 // Longer, smoother transition
        );
      }
    }
  }

  zoomToNode(node) {
    if (!this.graphRef?.current || !node) {
      return;
    }
    
    if (this.is3DMode && this.graphRef.current.cameraPosition) {
      // 3D mode: Aim at node from outside it
      const distance = 100;
      const distRatio = 1 + distance/Math.hypot(node.x, node.y, node.z);

      this.graphRef.current.cameraPosition(
        { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }, // new position
        node, // lookAt ({ x, y, z })
        2000  // ms transition duration
      );
    } else if (!this.is3DMode) {
      // 2D mode: Center on the node and zoom in
      if (this.graphRef.current.centerAt) {
        this.graphRef.current.centerAt(node.x, node.y, 1000);
      }
      if (this.graphRef.current.zoom) {
        this.graphRef.current.zoom(4, 1000);
      }
    }

    //simulationRef.current.stop(); // Keep the simulation stopped
    //setPauseSimulation(true);
  }

  
}

export const GraphLayoutPanel = ({ isMobile, layoutManager, graphData, mode3D, is3DMode, setIs3DMode, viewMode, dataSource, pcapLinkStyle, setPcapLinkStyle }) => {
  const classes = useStyles();
  // Always show the distribution toggle, but disable it in 2D view
  const distDisabled = !is3DMode;
  
  // Update the layoutManager's viewMode directly when needed
  // We avoid a useEffect dependency on layoutManager to prevent circular deps
  if (layoutManager && viewMode) {
    // This is safe to do here because the GraphLayoutPanel is always rendered
    // after layoutManager is fully initialized
    layoutManager.viewMode = viewMode;
  }

  return (
          <div id="leftPanel" className={classes.leftPanel}>

             {/* 2D/3D View Toggle */}
             {(!isMobile) && (
               <div style={{border: '2px solid yellow', marginBottom: '10px' }} >
                 <Tooltip title={is3DMode ? "Switch to 2D View" : "Switch to 3D View"}>
                   <IconButton 
                     onClick={() => {
                      // Store the time of this view mode change to prevent rapid toggling
                      const viewChangeTime = Date.now();
                      window._lastViewModeChange = viewChangeTime;
                      
                      const newIs3DMode = !is3DMode;
                      setIs3DMode(newIs3DMode);
                      
                      // Synchronize layout mode with view mode
                      if (newIs3DMode && !mode3D) {
                        // If switching to 3D view, set layout mode to 3D
                        layoutManager.setMode3D(true);
                      } else if (!newIs3DMode && mode3D) {
                        // If switching to 2D view, set layout mode to 2D
                        layoutManager.setMode3D(false);
                      }
                      
                      // Simple auto-fit when switching modes
                      // Avoid complex camera calculations that might cause blinking
                      if (!newIs3DMode) {
                        // 2D Mode: Fit all nodes in view
                        setTimeout(() => {
                          if (layoutManager.graphRef?.current?.zoomToFit) {
                            // Simple zoomToFit works well for 2D
                            layoutManager.graphRef.current.zoomToFit(400, 10);
                          }
                        }, 200);
                      } else {
                        // 3D Mode: No need to manually position
                        // The startCameraSpin function will handle camera positioning
                        setTimeout(() => {
                          if (layoutManager.startCameraSpin) {
                            layoutManager.startCameraSpin();
                          }
                        }, 300);
                      }
                      
                      // Store the timestamp to prevent other operations
                      // during this mode change
                      window._lastModeChangeTime = Date.now();
                    }} 
                     className={classes.toolIconStyle}
                     style={{backgroundColor: is3DMode ? 'rgba(0,255,0,0.2)' : 'rgba(255,255,0,0.2)'}}
                   >
                     {is3DMode ? <FaCube /> : <FaRegSquare />}
                   </IconButton>
                 </Tooltip>
               </div>
             )}
             
             {/* Distribution Mode Toggle (ALWAYS VISIBLE, just disabled in 2D view) */}
             <div style={{border: '2px solid white', marginBottom: '10px', display: 'flex', flexDirection: 'column'}}>
               <div style={{textAlign: 'center', fontSize: '10px', marginBottom: '2px', color: is3DMode ? '#fff' : '#888'}}>
                 Layout Mode
               </div>

               {/* 2D layout mode button */}
               <div style={{border: (!mode3D && is3DMode) ? '3px solid green' : 'none', margin: '2px 0'}}>
                 <Tooltip title={!is3DMode ? "Switch to 3D view first" : "2D layout for nodes"}>
                   <div> {/* Wrapper div to ensure tooltip works when button is disabled */}
                     <IconButton 
                       disabled={!is3DMode} 
                       onClick={() => layoutManager.setMode3D(false)} 
                       className={classes.toolIconStyle}
                       style={{opacity: !is3DMode ? 0.5 : 1}}
                     >
                       <FaRegSquare/>
                     </IconButton>
                   </div>
                 </Tooltip>
               </div>

               {/* 3D layout mode button */}
               <div style={{border: (mode3D && is3DMode) ? '3px solid green' : 'none'}}>
                 <Tooltip title={!is3DMode ? "Switch to 3D view first" : "3D layout for nodes"}>
                   <div> {/* Wrapper div to ensure tooltip works when button is disabled */}
                     <IconButton 
                       disabled={!is3DMode} 
                       onClick={() => layoutManager.setMode3D(true)} 
                       className={classes.toolIconStyle}
                       style={{opacity: !is3DMode ? 0.5 : 1}}
                     >
                       <FaCube />
                     </IconButton>
                   </div>
                 </Tooltip>
               </div>
              </div>
     
             {/* Organic / simulation layout button temporarily disabled */}

             {[
              {
                key: 'sphere',
                title: `Distribute Everything as a ${mode3D ? 'Sphere' : 'Circle'}`,
                icon: mode3D ? <FaGlobe /> : <FaCircle />,
                onClick: () => {
                  if (layoutManager.setLayoutMode) layoutManager.setLayoutMode('sphere');
                  layoutManager.handleDistributeAll(graphData);
                },
              },
              {
                key: 'pyramid',
                title: 'Distribute as Pyramid or Triangle',
                icon: <FaExclamationTriangle />,
                onClick: () => {
                  if (layoutManager.setLayoutMode) layoutManager.setLayoutMode('pyramid');
                  layoutManager.distributeNodesInPyramidOrTriangle(graphData);
                },
              },
              {
                key: 'tube',
                title: 'Distribute Everything around Tube',
                icon: <FaGripLines />,
                onClick: () => {
                  if (layoutManager.setLayoutMode) layoutManager.setLayoutMode('tube');
                  layoutManager.distributeConnectedNodesAroundTube(graphData);
                },
              },
              {
                key: 'grid',
                title: 'Distribute Everything as a Grid or Cube',
                icon: <FaTh />,
                onClick: () => {
                  if (layoutManager.setLayoutMode) layoutManager.setLayoutMode('grid');
                  layoutManager.distributeIconsInGridOrCube(graphData);
                },
              },
            ].map((btn) => (
              <Tooltip key={btn.key} title={btn.title}>
                <IconButton onClick={btn.onClick} className={classes.toolIconStyle2}>
                  {btn.icon}
                </IconButton>
              </Tooltip>
            ))}
 
             {(!isMobile) && ( 
               <div>
                 <Tooltip title="Distribute Icons in Straight Line">
                   <IconButton onClick={() => layoutManager.distributeIconsAsLine(graphData)} className={classes.toolIconStyle2}>
                     <FaEllipsisH /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Tokens"> 
                   <IconButton onClick={() => layoutManager.handleDistributeTokens(graphData)} className={classes.toolIconStyle2}>
                     <FaDotCircle /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute All Users">
                   <IconButton onClick={() => layoutManager.handleDistributeUsers(graphData)} className={classes.toolIconStyle2}>
                     <FaUserCircle /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Users around Token">
                   <IconButton onClick={() => layoutManager.distributeUsersAroundToken(graphData)} className={classes.toolIconStyle2}>
                     <FaCircleNotch /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Users around All Token">
                   <IconButton onClick={() => layoutManager.distributeUsersAroundAllTokens(graphData)} className={classes.toolIconStyle2}>
                     <FaObjectGroup /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Lock Positions">
                   <IconButton onClick={() => layoutManager.handleDistributeLock(graphData)} className={classes.toolIconStyle2}>
                     <FaLock /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Undo Distribute">
                   <IconButton onClick={() => layoutManager.handleDistributeUndo(graphData)} className={classes.toolIconStyle2}>
                     <FaUndo /> 
                   </IconButton>
                 </Tooltip>
               </div>
             )}
 
             <Tooltip title="Compress Graph">
               <IconButton onClick={() => layoutManager.handleCompressGraph(graphData)} className={classes.toolIconStyle2}>
                 <FaCompress /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Expand Graph">
               <IconButton onClick={() => layoutManager.handleExpandGraph(graphData)} className={classes.toolIconStyle2}>
                 <FaExpand /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Zoom In">
               <IconButton onClick={() => layoutManager.zoomIn()} className={classes.toolIconStyle2}>
                 <FaPlus /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Zoom Out">
               <IconButton onClick={() => layoutManager.zoomOut()} className={classes.toolIconStyle2}>
                 <FaMinus /> 
               </IconButton>
             </Tooltip>
 
           </div>
  );
}
